// node_modules
import { formatUtcOffset } from "../../../utils/timezoneOptions";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMobile } from "../../../hooks/useMobile";
import {
  useDeleteOrganizationMutation,
  useGetOrganizationListQuery,
  useImportOrganizationMutation,
  useLazyGetDeletedOrganizationListQuery,
  useLazyGetOrganizationDataQuery,
  usePermanentlyDeleteOrganizationMutation,
  useRecoverOrganizationMutation,
} from "../../../redux/services/organization";

// redux
import { setLoading } from "../../../redux/slices/app.slice";

// primereact components
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";

// context
import { ToastContext } from "../../../App";
import {
  parseOrganizationCSVToJson,
  parseOrganizationJSON,
} from "../../../utils/csvHelper";

// styles
import * as styles from "./Organizations.module.css";
import { useImportModal } from "../../../context/ImportModalContext";
import { ImportButton } from "../../../components/ImportButton/ImportButton";
import { ImportModal } from "../../../components/ImportModal/ImportModal";

const Organizations = () => {
  const { showImportModal, setShowImportModal } = useImportModal();
  const showToast = useContext(ToastContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile } = useMobile();
  const [data, setData] = useState([]);

  const {
    data: organizations,
    isSuccess: isGetOrganizationsSuccess,
    isLoading: isGetOrganizationsLoading,
    isError: isGetOrganizationsError,
  } = useGetOrganizationListQuery();
  const [
    deleteOrganization,
    {
      isSuccess: isDeleteOrganizationSuccess,
      isError: isDeleteOrganizationError,
      error: deleteOrganizationError,
    },
  ] = useDeleteOrganizationMutation();
  const [getOrganizationData] = useLazyGetOrganizationDataQuery();
  const [
    importOrganization,
    {
      isSuccess: isImportOrganizationSuccess,
      isError: isImportOrganizationError,
      error: importOrganizationError,
    },
  ] = useImportOrganizationMutation();
  const [
    getDeletedOrganizations,
    {
      data: deletedOrganizations,
      isSuccess: isGetDeletedOrganizationsSuccess,
      isError: isGetDeletedOrganizationsError,
    },
  ] = useLazyGetDeletedOrganizationListQuery();
  const [
    recoverOrganization,
    {
      isSuccess: isRecoverOrganizationSuccess,
      isError: isRecoverOrganizationError,
    },
  ] = useRecoverOrganizationMutation();
  const [
    permanentlyDeleteOrganization,
    {
      isSuccess: isPermanentlyDeleteOrganizationSuccess,
      isError: isPermanentlyDeleteOrganizationError,
    },
  ] = usePermanentlyDeleteOrganizationMutation();

  const uploadRef = useRef(null);
  const [id, setId] = useState(null);
  const [name, setName] = useState(null);
  const [deleteMode, setDeleteMode] = useState("delete");

  const goCreate = () => navigate("create");
  const goEdit = (rowData) => navigate(`${rowData.id}/edit`);

  const onFileUpload = async (e) => {
    try {
      const file = e.files[0];
      const text = await file.text();
      const json = parseOrganizationCSVToJson(text);
      importOrganization(json);
    } catch (e) {
      console.error(e);
    }
  };

  const downloadCSVFile = (content, filename) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToCSV = async (rowData) => {
    try {
      const data = await getOrganizationData(rowData.id, false).unwrap();

      let csvContent;
      try {
        csvContent = parseOrganizationJSON(data);
      } catch (e) {
        showToast(
          "error",
          "Organization",
          "Unable to export data (parse error)",
        );
        return;
      }

      downloadCSVFile(csvContent, `${rowData.name}_export.csv`);
    } catch (error) {
      showToast("error", "Organization", "Unable to export data");
    }
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (isGetOrganizationsSuccess) {
      dispatch(setLoading(false));

      setData((prev) => [...prev, ...organizations]);

      getDeletedOrganizations();
    }
  }, [isGetOrganizationsSuccess]);

  useEffect(() => {
    if (isGetOrganizationsError) {
      showToast("error", "Organization", "Unable to load organizations");
      dispatch(setLoading(false));
    }
  }, [isGetOrganizationsError]);

  useEffect(() => {
    let tempData = [];
    if (isGetOrganizationsSuccess) tempData = organizations;
    if (isGetDeletedOrganizationsSuccess)
      tempData = [...tempData, ...deletedOrganizations];

    setData(tempData);
  }, [
    isGetOrganizationsSuccess,
    organizations,
    isGetDeletedOrganizationsSuccess,
    deletedOrganizations,
  ]);

  useEffect(() => {
    if (isGetDeletedOrganizationsError) {
      showToast(
        "error",
        "Organization",
        "Unable to load deleted organizations",
      );
    }
  }, [isGetDeletedOrganizationsError]);

  useEffect(() => {
    if (isImportOrganizationSuccess) {
      showToast("success", "Organization", "Organization imported!");
      uploadRef.current.clear();
      setShowImportModal(false);
    }
  }, [isImportOrganizationSuccess]);

  useEffect(() => {
    if (isImportOrganizationError) {
      showToast(
        "error",
        importOrganizationError?.status,
        importOrganizationError?.data?.message ||
          (importOrganizationError?.data?.errors && "Incorrect data"),
      );
      uploadRef.current.clear();
    }
  }, [isImportOrganizationError]);

  useEffect(() => {
    if (isDeleteOrganizationSuccess) {
      showToast("success", "Organization", "Organization deleted!");
      setShowDeleteModal(false);
      getDeletedOrganizations();
    }
  }, [isDeleteOrganizationSuccess]);

  useEffect(() => {
    if (isDeleteOrganizationError) {
      showToast(
        "error",
        deleteOrganizationError?.status,
        deleteOrganizationError?.data?.message ||
          (deleteOrganizationError?.data?.errors && "Incorrect data"),
      );
    }
  }, [isDeleteOrganizationError]);

  useEffect(() => {
    if (isPermanentlyDeleteOrganizationSuccess) {
      showToast("success", "Organization", "Organization deleted!");
      setShowDeleteModal(false);
      getDeletedOrganizations();
    }
  }, [isPermanentlyDeleteOrganizationSuccess]);

  useEffect(() => {
    if (isPermanentlyDeleteOrganizationError) {
      showToast(
        "error",
        "Organization",
        "Unable to permanently delete organization",
      );
    }
  }, [isPermanentlyDeleteOrganizationError]);

  useEffect(() => {
    if (isRecoverOrganizationSuccess) {
      showToast("success", "Organization", "Organization recovered!");
      getDeletedOrganizations();
    }
  }, [isRecoverOrganizationSuccess]);

  useEffect(() => {
    if (isRecoverOrganizationError) {
      showToast("error", "Organization", "Unable to recover organization");
    }
  }, [isRecoverOrganizationError]);

  const openDeleteModal = (rowData) => () => {
    setId(rowData.id);
    setName(rowData.name);
    setDeleteMode(rowData.isDeleted ? "permanently" : "delete");
    setShowDeleteModal(true);
  };

  const onPermanentlyDelete = () => {
    permanentlyDeleteOrganization(id);
  };

  const onDelete = () => {
    deleteOrganization(id);
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <div className={styles.cellSoundWrapper}>
        {rowData.isDeleted ? (
          <Button
            icon="pi pi-refresh"
            className={`p-button-text ${styles.soundBtn}`}
            onClick={() => recoverOrganization(rowData.id)}
          />
        ) : (
          <Button
            icon="pi pi-pencil"
            className={`p-button-text ${styles.soundBtn}`}
            onClick={() => goEdit(rowData)}
          />
        )}
        {/* <Button
          disabled={rowData.isDeleted}
          icon="pi pi-clone"
          className={`p-button-text ${styles.soundBtn}`}
          onClick={openModal("copy", rowData)}
        /> */}
        <Button
          icon="pi pi-upload"
          className={`p-button-text ${styles.exportBtn}`}
          onClick={() => exportToCSV(rowData)}
        />
        <Button
          icon="pi pi-trash"
          className={`p-button-text ${styles.deleteBtn}`}
          onClick={openDeleteModal(rowData)}
        />
      </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <div className={`${styles.header}`}>
        <div className={styles.headerContentContainer}>
          <h2>Organizations</h2>
          <ImportButton onClick={() => setShowImportModal(true)} />
        </div>

        <Button className={styles.createBtn} onClick={goCreate}>
          <span>ADD ORGANIZATIONS</span> <i className="pi pi-plus" />
        </Button>
      </div>
      {organizations &&
        (isMobile ? (
          <div className={styles.cardList}>
            {data.map((organization) => (
              <div key={organization.email} className={styles.cardItem}>
                <div className={styles.cardContent}>
                  <div className={styles.cardBadge}>
                    {organization.subdomain}
                  </div>
                  <div className={styles.cardInfo}>
                    <div className={styles.name}>{organization.name}</div>
                    <div className={styles.subfield}>
                      {formatUtcOffset(organization.timezone)} {organization.timezone}
                    </div>
                  </div>
                </div>
                <div className={styles.actions}>
                  <Button
                    icon="pi pi-upload"
                    className={`p-button-text ${styles.exportBtnMobile}`}
                    onClick={() => exportToCSV(organization)}
                  />
                  {organization.isDeleted ? (
                    <button
                      className={styles.cardEditBtn}
                      onClick={() => recoverOrganization(organization.id)}
                    >
                      <i className="pi pi-refresh" />
                    </button>
                  ) : (
                    <button
                      className={styles.cardEditBtn}
                      onClick={() => goEdit(organization)}
                    >
                      <i className="pi pi-pencil" />
                    </button>
                  )}
                  {/* <button
                    className={styles.cardEditBtn}
                    disabled={organization.isDeleted}
                    onClick={openModal("copy", organization)}
                  >
                    <i className="pi pi-clone" />
                  </button> */}
                  <button
                    className={styles.cardDeleteBtn}
                    onClick={openDeleteModal(organization)}
                  >
                    <i className="pi pi-trash" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <DataTable value={data} rows={6} loading={isGetOrganizationsLoading}>
            <Column
              className={styles.hoverableCell}
              field="name"
              header="Name"
              body={(rowData) => {
                return <div className={styles.cellInsider}>{rowData.name}</div>;
              }}
            />

            <Column
              className={styles.hoverableCell}
              field="subdomain"
              header="Subdomain"
              style={{ width: "276px" }}
              body={(rowData) => {
                return (
                  <div className={styles.cellInsider}>{rowData.subdomain}</div>
                );
              }}
            />

            <Column
              className={styles.hoverableCell}
              field="timezone"
              header="Timezone"
              style={{ width: "276px" }}
              body={(rowData) => {
                return (
                  <div className={styles.cellInsider}>
                    {formatUtcOffset(rowData.timezone)} {rowData.timezone}
                  </div>
                );
              }}
            />
            <Column
              className={styles.hoverableCell}
              body={actionBodyTemplate}
              header="Actions"
              style={{ width: "242px" }}
            />
          </DataTable>
        ))}

      <Dialog
        closable={false}
        blockScroll
        header={
          <div style={{ width: "100%", textAlign: "center" }}>
            Are You Sure?
          </div>
        }
        visible={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
        }}
        footer={() => (
          <div className="flex flex-column gap-2 justify-content-end">
            <Button
              label="Delete"
              className={`p-button-success ${styles.deleteDialogBtn}`}
              onClick={
                deleteMode === "permanently" ? onPermanentlyDelete : onDelete
              }
            />
            <Button
              label="Cancel"
              text
              className={`p-button-success ${styles.cancelDialogBtn}`}
              onClick={() => {
                setShowDeleteModal(false);
              }}
            />
          </div>
        )}
        className={`p-fluid ${styles.deleteModal}`}
      >
        <div className={styles.text}>
          <span>
            You want to {deleteMode === "permanently" ? "permanently" : ""}{" "}
            delete the organization: <strong>{name}</strong>.
          </span>
          Confirm this action by pressing ‘Delete’ or cancel it.
        </div>
      </Dialog>

      {/* Import Modal */}
      <ImportModal
        uploadRef={uploadRef}
        showImportModal={showImportModal}
        setShowImportModal={setShowImportModal}
        onFileUpload={onFileUpload}
      />
    </div>
  );
};

export default Organizations;
